import { Box, Tab, Tabs, Typography } from '@mui/material'
import Paper from '@mui/material/Paper'
import type { FC, ReactElement } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import TabPanel from '@/components/layouts/TabPanel'
import type { ActivationGroup, ActivationGroupUuid } from '@/features/activationGroup/types'
import CapacityOfferChart from '@/features/bidding/components/capacity/offer/CapacityOfferChart'
import CreateBidForm from '@/features/bidding/components/capacity/offer/CreateBidForm'
import { useBidOfferDataContext } from '@/features/bidding/contexts/BidOfferDataContext'
import getBidMetaData from '@/features/bidding/utils/getBidMetaData'

const BidOfferTabs = () => {
  const { eligibleActivationGroups, tabMetadata } = useBidOfferDataContext()
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(0)

  if (!eligibleActivationGroups.length) {
    return (
      <Paper data-testid={'no-groups-message'} sx={{ p: 2, mt: 2 }}>
        <Typography align={'center'} color="text.secondary" variant="h3">
          {t('bidding.error.no_groups_available.title')}
        </Typography>
        <Typography align={'center'} color="text.secondary" variant="body1">
          {t('bidding.error.no_groups_available.message')}
        </Typography>
      </Paper>
    )
  }

  return (
    <>
      <Box sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          scrollButtons="auto"
          style={{ minHeight: 0 }}
          value={activeTab}
          variant="scrollable"
          onChange={(_, newValue) => setActiveTab(newValue)}
        >
          {eligibleActivationGroups.map((activationGroup, index) => (
            <Tab
              key={activationGroup.uuid}
              icon={getTabIcon(tabMetadata.tabIcons, activationGroup.uuid)}
              iconPosition="start"
              label={activationGroup.code}
              style={{ minHeight: 0 }}
              value={index}
            />
          ))}
        </Tabs>
      </Box>

      {eligibleActivationGroups.map((activationGroup, index) => (
        <ActivationGroupContent
          key={activationGroup.uuid}
          activationGroup={activationGroup}
          activeTab={activeTab}
          index={index}
        />
      ))}
    </>
  )
}

const getTabIcon = (
  tabIcons: Map<ActivationGroupUuid, ReactElement>,
  activationGroupUuid: ActivationGroupUuid,
): ReactElement | undefined => {
  return tabIcons.get(activationGroupUuid)
}

type ActivationGroupContentProps = {
  activationGroup: ActivationGroup
  index: number
  activeTab: number
}

const ActivationGroupContent: FC<ActivationGroupContentProps> = ({ activationGroup, index, activeTab }) => {
  const { groupParams } = useBidOfferDataContext()

  return (
    <TabPanel index={index} value={activeTab}>
      <CapacityOfferChart
        activationGroupUuid={activationGroup.uuid}
        deliveryDay={groupParams.deliveryDay}
        priceArea={activationGroup.portfolio.priceArea}
      />
      <CreateBidForm bidMetaData={getBidMetaData(groupParams.deliveryDay, groupParams.bidType, activationGroup)} />
    </TabPanel>
  )
}

export default BidOfferTabs
